import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControlLabel,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { CheckCircle as DoneIcon } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { useAuth } from "../contexts/AuthContext";
import TimesheetService from "../services/Timesheet.service";
import WorkOrderService from "../services/WorkOrder.service";

// Opened from the "Log my hours" QR code on a printed work traveler. The work
// order + step come from the QR, the times are pre-filled, and the entry is
// saved as a normal Shop timesheet segment linked to the traveler — so all the
// employee has to do is check the times and tap Save.

// Steps in these statuses can still accept time (matches WorkOrderTravelerPicker).
const OPEN_STATUSES = ["pending", "in_progress", "submitted"];
// A standard shift; the employee only changes these if their day was different.
const DEFAULT_START = "06:00";
const DEFAULT_END = "14:30";

function todayString() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function toISOWeekString(dateStr) {
    const [y, m, day] = dateStr.split("-").map(Number);
    const d = new Date(Date.UTC(y, m - 1, day));
    const dow = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dow);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    return `${d.getUTCFullYear()}W${String(week).padStart(2, "0")}`;
}

const toMinutes = (t) => {
    const [h, m] = String(t).slice(0, 5).split(":").map(Number);
    return h * 60 + m;
};

const fromMinutes = (total) => {
    const clamped = Math.max(0, Math.min(total, 23 * 60 + 45));
    return `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`;
};

const roundToQuarter = (t) => fromMinutes(Math.round(toMinutes(t) / 15) * 15);

function timeStringToDate(value) {
    const d = new Date();
    d.setHours(Math.floor(toMinutes(value) / 60), toMinutes(value) % 60, 0, 0);
    return d;
}

function formatTime(t) {
    return format(timeStringToDate(t), "h:mm a");
}

// Mirrors the backend's duration calc: hours between start/end, minus 30 min for lunch.
function computeHours(start, end, lunch) {
    const minutes = toMinutes(end) - toMinutes(start);
    if (minutes <= 0) return 0;
    return Math.max(0, minutes / 60 - (lunch ? 0.5 : 0));
}

export default function LogTime() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [traveler, setTraveler] = useState(null);
    const [workOrder, setWorkOrder] = useState(null);
    const [shopTypeId, setShopTypeId] = useState(null);
    const [daySegments, setDaySegments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    const [date, setDate] = useState(todayString());
    const [start, setStart] = useState(DEFAULT_START);
    const [end, setEnd] = useState(DEFAULT_END);
    const [lunch, setLunch] = useState(true);

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");
    const [saved, setSaved] = useState(null);

    // Traveler + the Shop work type, once.
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const [travRes, typesRes] = await Promise.all([
                    WorkOrderService.getTravelerByToken(token),
                    TimesheetService.getWorkTypes(),
                ]);
                if (!active) return;
                setTraveler(travRes.data);
                const shop = (typesRes.data ?? []).find(
                    (t) => String(t.name || "").trim().toLowerCase() === "shop",
                );
                setShopTypeId(shop?.id ?? null);
                WorkOrderService.getWorkOrder(travRes.data.manufacture_order)
                    .then((res) => active && setWorkOrder(res.data))
                    .catch(() => {});
            } catch (err) {
                if (!active) return;
                setLoadError(
                    err.response?.status === 404
                        ? "This QR code doesn't match any work order step. It may have been reprinted."
                        : "Couldn't load this work order. Check your connection and try again.",
                );
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [token]);

    // The employee's other entries on the chosen day, to stop overlapping
    // (double-counted) time.
    useEffect(() => {
        if (!user?.id || !date) return;
        let active = true;
        TimesheetService.getSegments(toISOWeekString(date))
            .then((res) => {
                if (!active) return;
                const all = res.data.results ?? res.data;
                const mine = all
                    .filter((s) => s.user === user.id && s.date === date)
                    .sort((a, b) => a.start_time.localeCompare(b.start_time));
                setDaySegments(mine);
            })
            .catch(() => active && setDaySegments([]));
        return () => {
            active = false;
        };
    }, [date, user?.id]);

    const hours = computeHours(start, end, lunch);

    const overlap = useMemo(
        () =>
            daySegments.find(
                (s) => toMinutes(start) < toMinutes(s.end_time) && toMinutes(s.start_time) < toMinutes(end),
            ),
        [daySegments, start, end],
    );

    const problem =
        toMinutes(end) <= toMinutes(start)
            ? "End time must be after start time."
            : overlap
              ? `You already logged ${formatTime(overlap.start_time)} – ${formatTime(overlap.end_time)}${
                    overlap.work_order_number ? ` on ${overlap.work_order_number}` : ""
                } that day. Change the times so they don't overlap.`
              : !shopTypeId
                ? 'The "Shop" work type is missing. Ask your supervisor.'
                : "";

    const handleSave = async () => {
        setSaving(true);
        setSaveError("");
        try {
            await TimesheetService.createSegment({
                user: user.id,
                date,
                start_time: start,
                end_time: end,
                lunch,
                segment_type_id: shopTypeId,
                project: null,
                quote: null,
                work_order_traveler: traveler.id,
                // The backend derives project + is_project_labor from the traveler's work order.
                is_project_labor: false,
                travel_duration: 0,
                notes: "",
            });
            setSaved({ hours, start, end, date });
        } catch (err) {
            setSaveError(
                err.response?.data?.detail ||
                    err.response?.data?.non_field_errors?.[0] ||
                    "Couldn't save your hours. Try again.",
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
            </Box>
        );
    }

    if (loadError || !traveler) {
        return (
            <Box p={2}>
                <Alert severity="error">{loadError || "Something went wrong."}</Alert>
            </Box>
        );
    }

    const header = (
        <>
            <Typography variant="overline" color="text.secondary">
                Log my hours
            </Typography>
            <Typography variant="h5" fontWeight={800} sx={{ fontFamily: "Montserrat, sans-serif" }}>
                {traveler.work_order_number}
            </Typography>
            <Typography variant="h6" fontWeight={700}>
                Step {traveler.step_number}: {traveler.step_name}
            </Typography>
            {workOrder && (
                <Typography variant="body2" color="text.secondary">
                    {workOrder.part_display}
                    {workOrder.part_description ? ` — ${workOrder.part_description}` : ""}
                </Typography>
            )}
        </>
    );

    if (saved) {
        return (
            <Box sx={{ p: 2, maxWidth: 520, mx: "auto" }}>
                <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: "success.main", borderRadius: 3, textAlign: "center" }}>
                    <DoneIcon color="success" sx={{ fontSize: 64 }} />
                    <Typography variant="h5" fontWeight={800} sx={{ fontFamily: "Montserrat, sans-serif", mt: 1 }}>
                        {saved.hours.toFixed(2)} hours saved
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                        {traveler.work_order_number} · Step {traveler.step_number}: {traveler.step_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {saved.date === todayString() ? "Today" : saved.date}, {formatTime(saved.start)} – {formatTime(saved.end)}
                    </Typography>
                    <Stack spacing={1.5} sx={{ mt: 3 }}>
                        <Button variant="contained" size="large" onClick={() => navigate("/")}>
                            Done
                        </Button>
                        <Button onClick={() => navigate("/timesheets")}>View my timesheet</Button>
                    </Stack>
                </Paper>
            </Box>
        );
    }

    if (!OPEN_STATUSES.includes(traveler.status)) {
        return (
            <Box sx={{ p: 2, maxWidth: 520, mx: "auto" }}>
                <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: "divider", borderRadius: 3 }}>
                    {header}
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        This step is {String(traveler.status).replace(/_/g, " ")} and can't take more hours.
                        Ask your supervisor which step to log your time to.
                    </Alert>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, maxWidth: 520, mx: "auto" }}>
            <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: "divider", borderRadius: 3 }}>
                {header}

                <Divider sx={{ my: 2 }} />

                <Stack spacing={2.5}>
                    <TextField
                        label="Date"
                        type="date"
                        value={date}
                        onChange={(e) => e.target.value && setDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Stack direction="row" spacing={2}>
                            <TimePicker
                                label="Started"
                                value={timeStringToDate(start)}
                                onChange={(v) => v && setStart(roundToQuarter(format(v, "HH:mm")))}
                                minutesStep={15}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                            <TimePicker
                                label="Finished"
                                value={timeStringToDate(end)}
                                onChange={(v) => v && setEnd(roundToQuarter(format(v, "HH:mm")))}
                                minutesStep={15}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        </Stack>
                    </LocalizationProvider>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={lunch}
                                onChange={(e) => setLunch(e.target.checked)}
                            />
                        }
                        label="I took a 30 min lunch in this time"
                    />

                    <Box sx={{ textAlign: "center", py: 1 }}>
                        <Typography
                            variant="h3"
                            fontWeight={800}
                            color={problem ? "text.disabled" : "primary"}
                            sx={{ fontFamily: "Montserrat, sans-serif" }}
                        >
                            {hours.toFixed(2)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            hours on this step
                        </Typography>
                    </Box>

                    {problem && <Alert severity="error">{problem}</Alert>}
                    {saveError && <Alert severity="error">{saveError}</Alert>}

                    <Button
                        variant="contained"
                        size="large"
                        disabled={!!problem || saving || hours <= 0}
                        onClick={handleSave}
                        sx={{ py: 1.5, fontSize: "1.1rem" }}
                    >
                        {saving ? <CircularProgress size={24} color="inherit" /> : `Save ${hours.toFixed(2)} hours`}
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
}
